import OpenAI from 'openai'

import { getIamToken } from './genericApi'
import { getInstanceIdFromUrl } from './getInstanceIdFromUrl'

/**
 * Shared GenAI gateway client for all CRM call sites — the AiChatBot streaming
 * engine (`engine/conversation.ts`) and single-request callers like CSV import.
 *
 * Auth pattern: a fake API key plus a custom `fetch` that injects a fresh
 * per-request IAM token via `getIamToken(getInstanceIdFromUrl())` — CRM users have no
 * Scrivito org account and the token cannot be awaited in the constructor.
 *
 * `aiBackendConnector.getClient` is still a separate variant that prefers the
 * Scrivito editor token; folding it onto this client is a later step.
 */
export const GEN_AI_BASE_URL = 'https://api.justrelate.com/gen-ai/instances'

export function createGenAiClient(): OpenAI {
  const instanceId = getInstanceIdFromUrl()
  if (!instanceId) {
    throw new Error('Cannot create GenAI client: no instance id in URL')
  }

  return new OpenAI({
    // Fake key — the authorization header is overwritten in the fetch below.
    apiKey: 'dont-use-this-key',
    baseURL: `${GEN_AI_BASE_URL}/${instanceId}/v1`,
    defaultHeaders: { Accept: '*/*', Prefer: 'cache-ttl=300' },
    dangerouslyAllowBrowser: true,

    fetch: async (url, init) => {
      const headers = new Headers(init?.headers)
      for (const key of headers.keys()) {
        if (key.toLowerCase().startsWith('x-')) {
          headers.delete(key)
        }
      }
      headers.delete('authorization')
      headers.set('content-type', 'application/json')
      const token = await getIamToken(instanceId)
      headers.set('authorization', `Bearer ${token}`)
      return fetch(url, { ...init, headers })
    },
  })
}
